"use client";

import { apiGetOauthUrl } from "@/api/auth";
import GoogleIcon from "@/assets/global/auth/google-login-icon.svg";
import GoogleColorIcon from "@/assets/global/google.svg";
import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/showToast";
import { SmartImage } from "@/components/ui/smart-image";
import { useThemeMode } from "@/features/theme/theme-provider";
import { cn } from "@/lib/utils";
import { useState } from "react";
import { OAuthLoadingModal } from "./OAuthLoadingModal";

const GOOGLE_LOGIN_SOURCE = "google";
const GOOGLE_OAUTH_POPUP_NAME = "predik_google_oauth";
const socialList = [ { key: GOOGLE_LOGIN_SOURCE } ];

function openOAuthPopup() {
    const width = 480;
    const height = 720;
    const left = Math.max( 0, window.screenX + ( window.outerWidth - width ) / 2 );
    const top = Math.max( 0, window.screenY + ( window.outerHeight - height ) / 2 );

    return window.open(
        "about:blank",
        GOOGLE_OAUTH_POPUP_NAME,
        `popup=yes,width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`,
    );
}

interface SocialLoginGoogleProps {
    className?: string;
}

export function SocialLoginGoogle( { className }: SocialLoginGoogleProps ) {
    const { themeMode } = useThemeMode();
    const [ isLoading, setIsLoading ] = useState( false );
    const [ loadingSource, setLoadingSource ] = useState( "" );
    const googleIcon = themeMode === "dark" ? GoogleColorIcon : GoogleIcon;

    const handleSocialLogin = async ( source: string ) => {
        const popup = openOAuthPopup();

        if ( !popup ) {
            toast.error( "Please allow pop-ups to continue with Google" );
            return;
        }

        try {
            setIsLoading( true );
            setLoadingSource( source );

            const res = await apiGetOauthUrl( { url: location.href, source } );

            const [ ok, data ] = res;
            const oauthUrl = data?.data;
            if ( !ok || !oauthUrl ) {
                popup.close();
                return;
            }

            popup.location.href = oauthUrl;
            popup.focus();
        } catch ( error ) {
            popup.close();
            console.error( "Social login failed", error );
        } finally {
            setIsLoading( false );
            setLoadingSource( "" );
        }
    };

    return (
        <>
            <OAuthLoadingModal open={isLoading} source={loadingSource} />

            {socialList.map( item => {
                const isItemLoading = isLoading && loadingSource === item.key;

                return (
                    <Button
                        key={item.key}
                        type="button"
                        loadingText="Signing in..."
                        className={cn(
                            "h-12 w-full rounded-lg bg-brand-button-selected py-3 text-sm font-medium leading-6 tracking-[0.15px] text-surface-card hover:opacity-90 active:opacity-80 disabled:opacity-70",
                            className,
                        )}
                        aria-label="Login"
                        onClick={() => handleSocialLogin( item.key )}
                        disabled={isItemLoading}
                    >
                        <SmartImage
                            src={googleIcon}
                            alt=""
                            aria-hidden={true}
                            className="size-6 shrink-0 object-contain"
                        />
                        <span>Sign in with Google</span>
                    </Button>
                );
            } )}
        </>
    );
}
